/****************
 * SPEED RUN: AN ADMIN SEES COPYFI AS A NEWCOMER, WITHOUT A NEW ACCOUNT.
 * Guillaume 2026-10-26 (live review #143): "Admin sidebar button 'Speed run':
 * pretend I am a newcomer seeing CopyFi for the first time, without making a
 * new account."
 *
 * While it runs, in this browser only:
 *   - the onboarding state (chooser seen, guides, Get Started progress) is a
 *     separate newcomer copy, never written to the account (lib/onboarding.ts);
 *   - sessions and milestones made BEFORE the run are hidden from the screens,
 *     so Get Started, the welcome chooser and the Lite/Pro builder lock behave
 *     as for a new account; anything made during the run shows as usual;
 *   - the app is in Lite, the mode a new account starts in.
 * Leaving restores the previous mode. The account and its data are untouched:
 * what the admin does during the run (a paper session, a backtest) is real.
 * Phone twin: mobile-app/src/lib/speedRun.ts (same keys).
 ****************/

import { runReachedPro } from './speedRunEngine.core';

const KEY = 'copyfi.speedRun';
const MODE_BEFORE_KEY = 'copyfi.speedRun.modeBefore';
export const SPEED_RUN_EVENT = 'copyfi:speedrun';
// The phone's native app has no browser storage or events: there a run never starts
// (the admin's speed run is a web and /m tool); every access below is guarded.
const announce = () => {
  try { if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') window.dispatchEvent(new Event(SPEED_RUN_EVENT)); } catch { /* native */ }
};

interface SpeedRunState { startedAt: string }

function read(): SpeedRunState | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as SpeedRunState) : null;
  } catch {
    return null;
  }
}

/** The moment the run started, or null when not in a speed run. */
export function speedRunStartedAt(): Date | null {
  const s = read();
  return s ? new Date(s.startedAt) : null;
}

export function inSpeedRun(): boolean {
  return read() !== null;
}

/** The id the onboarding state is kept under: a separate newcomer copy during a run. */
export function onboardingIdFor(userId: string): string {
  return inSpeedRun() ? `${userId}#speedrun` : userId;
}

/** Hide what existed before the run (sessions, milestones): a newcomer has none. */
export function madeDuringRun(createdAt: string | null | undefined): boolean {
  const start = speedRunStartedAt();
  if (!start) return true;
  if (!createdAt) return false;
  return new Date(createdAt).getTime() >= start.getTime();
}

export function startSpeedRun(currentMode: string | null): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ startedAt: new Date().toISOString() }));
    localStorage.setItem(MODE_BEFORE_KEY, currentMode ?? '');
    // Every run starts fresh: the newcomer copy of the onboarding state goes.
    for (const k of Object.keys(localStorage)) {
      if (k.includes('#speedrun') || k === RUN_ACH_KEY || k === DONE_KEY || (k.startsWith(`${KEY}.`) && k !== MODE_BEFORE_KEY)) localStorage.removeItem(k);
    }
  } catch { /* private mode: nothing to run */ }
  announce();
}

/** Ends the run; returns the app mode to restore. */
export function endSpeedRun(): string | null {
  let mode: string | null = null;
  try {
    mode = localStorage.getItem(MODE_BEFORE_KEY) || null;
    localStorage.removeItem(KEY);
    localStorage.removeItem(MODE_BEFORE_KEY);
  } catch { /* nothing stored */ }
  announce();
  return mode;
}

/*
 * The newcomer's achievements and quizzes (round 9, speed run gaps). During a
 * run every achievement reads as not started and no quiz as taken; what the
 * admin follows or shows during the run is kept here, in this browser, never
 * saved to the account (like the onboarding copy above).
 */
const RUN_ACH_KEY = 'copyfi.speedRun.achievements';

export function runAchievementPrefs(): { goals: string[]; showcase: string[] } {
  try {
    if (typeof localStorage === 'undefined') return { goals: [], showcase: [] };
    const raw = localStorage.getItem(RUN_ACH_KEY);
    const v = raw ? (JSON.parse(raw) as { goals?: string[]; showcase?: string[] }) : {};
    return { goals: v.goals ?? [], showcase: v.showcase ?? [] };
  } catch {
    return { goals: [], showcase: [] };
  }
}

export function saveRunAchievementPrefs(body: { goals?: string[]; goal?: string; showcase?: string[] }): void {
  const cur = runAchievementPrefs();
  const goals = body.goals ?? (body.goal !== undefined ? (body.goal ? [body.goal] : []) : cur.goals);
  const next = { goals, showcase: body.showcase ?? cur.showcase };
  try { localStorage.setItem(RUN_ACH_KEY, JSON.stringify(next)); } catch { /* private mode */ }
}

/** An achievements answer as a newcomer sees it: nothing reached, the run's own choices. */
export function asNewcomerAchievements<T extends { achievements: { key?: string; tier: number; have?: number; tiers?: number[] }[]; showcase: string[]; goal: string; goals?: string[] }>(s: T, runCounts: Record<string, number> = {}): T {
  const p = runAchievementPrefs();
  // What the run itself did counts (11-02 audit: "Sessions launched 0/1" right after the run's paper session).
  const reached = (have: number, tiers: number[] = []) => tiers.filter((n) => have >= n).length;
  return {
    ...s,
    achievements: s.achievements.map((a) => {
      const have = (a.key && runCounts[a.key]) || 0;
      return { ...a, tier: reached(have, a.tiers), have };
    }),
    showcase: p.showcase,
    goal: p.goals[0] ?? '',
    goals: p.goals,
  };
}

/****************
 * A SPEED RUN IS COMPLETE once its first session starts (Guillaume, 10-31:
 * "CopyFi Pro ... should stay locked until speedrun complete"). Until then the
 * run is practice only: no broker key, no Telegram, no switch to Pro.
 ****************/
const DONE_KEY = 'copyfi.speedRun.done';

export function speedRunComplete(): boolean {
  try { return typeof localStorage !== 'undefined' && localStorage.getItem(DONE_KEY) === '1'; } catch { return true; }
}

/** Marks the run complete (a session started during it). */
export function markSpeedRunComplete(): void {
  if (!inSpeedRun()) return;
  try { localStorage.setItem(DONE_KEY, '1'); } catch { /* private mode */ }
  announce();
}

/** Inside a speed run that has not reached CopyFi Pro yet (Guillaume 11-01: Pro
 *  opens at the end of the path, lib/speedRunPath.core.ts RUN_PRO_STEP). */
export function speedRunLocked(): boolean {
  return inSpeedRun() && !runReachedPro();
}

/****************
 * THE A-TO-Z PATH'S SHARED STATE (lib/speedRunPath.core.ts, 11-01).
 * Screens send signals and record the run's trader and strategy here; the
 * guide engine listens, and the My Copies forms read them for their prefills.
 ****************/
export const RUN_SIGNAL_EVENT = 'copyfi:speedrun:signal';
/** "Continue the tutorial" (the CopyFi Pro lock pop-up): the guide shows its current step again. */
export const RUN_RESUME_EVENT = 'copyfi:speedrun:resume';
const RUN_PICKS_KEY = 'copyfi.speedRun.picks';

/** A screen did what a step waits for (RUN_SIGNAL in lib/speedRunPath.core.ts). No-op outside a run. */
export function runSignal(name: string): void {
  if (!inSpeedRun()) return;
  try { if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') window.dispatchEvent(new CustomEvent(RUN_SIGNAL_EVENT, { detail: name })); } catch { /* native */ }
}

export function resumeSpeedRun(): void {
  try { if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') window.dispatchEvent(new Event(RUN_RESUME_EVENT)); } catch { /* native */ }
}

interface RunPicks { traderUid?: string; traderName?: string; strategyId?: string; strategyName?: string; backtestId?: string }

/** The trader favourited and the strategy saved during the run (for the My Copies prefills). */
export function runPicks(): RunPicks {
  try { return JSON.parse(localStorage.getItem(RUN_PICKS_KEY) || '{}') as RunPicks; } catch { return {}; }
}

export function setRunPick(p: RunPicks): void {
  if (!inSpeedRun()) return;
  // A new backtest started in the run: the last one's failure is behind it.
  if (p.backtestId) setRunBacktestFailed(false);
  try { localStorage.setItem(RUN_PICKS_KEY, JSON.stringify({ ...runPicks(), ...p })); } catch { /* private mode */ }
}

/****************
 * THE ORB'S CONVERSATION DURING A RUN (11-02 live audit: the run showed his own
 * chat, which is a synced account setting). Kept in this browser under a run
 * key, which a new run clears; the account's own conversation is not touched.
 ****************/
const RUN_ORB_KEY = 'copyfi.speedRun.orb';

export function readRunOrb<T>(): T[] {
  try { return JSON.parse(localStorage.getItem(RUN_ORB_KEY) || '[]') as T[]; } catch { return []; }
}

export function writeRunOrb<T>(turns: T[]): void {
  try { localStorage.setItem(RUN_ORB_KEY, JSON.stringify(turns.slice(-60))); } catch { /* private mode */ }
}

/** The run's own counts for its achievements: sessions started, traders copied, backtests finished during the run. */
export function runAchievementCounts(subs: { created_at?: string; trader_uid?: string }[], backtests: { created_at?: string; status?: string }[]): Record<string, number> {
  const mine = (subs || []).filter((x) => madeDuringRun(x.created_at));
  return {
    sessions: mine.length,
    traders_copied: new Set(mine.map((x) => x.trader_uid).filter(Boolean)).size,
    backtests: (backtests || []).filter((b) => madeDuringRun(b.created_at) && b.status === 'done').length,
  };
}

/****************
 * THE RUN'S BACKTEST FAILED (11-02 audit: #64 was stopped at 20 min and the run
 * sat on "Your backtest is still running" for good). The watcher takes the run
 * back to its backtest step, which then says so; a new backtest clears it.
 ****************/
const RUN_BT_FAILED_KEY = 'copyfi.speedRun.backtestFailed';
export function runBacktestFailed(): boolean {
  try { return typeof localStorage !== 'undefined' && localStorage.getItem(RUN_BT_FAILED_KEY) === '1'; } catch { return false; }
}
export function setRunBacktestFailed(failed: boolean): void {
  try { if (failed) localStorage.setItem(RUN_BT_FAILED_KEY, '1'); else localStorage.removeItem(RUN_BT_FAILED_KEY); } catch { /* private mode */ }
}
